import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import React, { useContext, useEffect, useState } from "react";
import NumberFormat from "react-number-format";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";
import {
   Button, ButtonGroup, Col, Form, FormFeedback, FormGroup,
   Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row
} from "reactstrap";
import { DesktopContainer } from "../layouts";
import { api, constants, helpers, UserContext } from "../utils";

export default function AddressEditor(_props) {
   const { context, id } = useParams();
   const navigate = useNavigate();
   const userCtx = useContext(UserContext);
   const [accountInfo, setAccountInfo] = useState();
   const [addresses, setAddresses] = useState([]);
   const [activeAddress, setActiveAddress] = useState({});
   const [showStateDropdown, setShowStateDropdown] = useState(false);
   const [stateList, setStateList] = useState();
   const [countryList, setCountryList] = useState();
   const [emailList, setEmailList] = useState();
   const [newEmail, setNewEmail] = useState();
   const [emailInvalid, setEmailInvalid] = useState(false);
   const [editIndex, setEditIndex] = useState(null);
   const [editCopy, setEditCopy] = useState(null);
   const [emailToDelete, setEmailToDelete] = useState(null);
   let modalClassName = userCtx.theme ? `${userCtx.theme}` : "lightTheme";

   useEffect(
      refreshData
      // eslint-disable-next-line react-hooks/exhaustive-deps
      , []);

   function allowEmailEdit(index) {
      setEditIndex(index);
      setEditCopy(emailList[index]);
   }

   function cancelEmailEdit() {
      setEditIndex(null);
      setEditCopy(null);
   }

   function deleteEmail() {
      const emailCopy = _.filter(emailList, (email) => email !== emailToDelete);
      setEmailList(emailCopy);
      setEmailToDelete(null);
   }

   function handleChange(field, value) {
      const addressCopy = _.clone(activeAddress);
      addressCopy[field] = value;
      setActiveAddress(addressCopy);
   }

   function handleCountryChange(e) {
      if (e.value === "US") {
         setShowStateDropdown(true);
         setStateList(constants.STATES);
      } else if (e.value === "CA" || e.value === "CANADA") {
         setShowStateDropdown(true);
         setStateList(constants.PROVINCES);
      } else {
         setShowStateDropdown(false);
         setStateList(null);
      }
      handleChange("state", null);
      handleChange("country", e.label);
      handleChange("countryCode", e.value);
   }

   function handleSubmit(e) {
      e.preventDefault();
      if (!activeAddress.addressCode) return;

      // This fixes where the extension is part of the phone field on address load. Saving with no changes
      // was causing double extensions to be saved, sometimes throwing an error if it got too large.
      var tmpPhone = activeAddress.phone.length > 10 ? activeAddress.phone.substring(0, 10) : activeAddress.phone;
      const payload = {
         AccountId: id,
         AddressCode: activeAddress.addressCode,
         Address1: activeAddress.address1,
         Address2: activeAddress.address2,
         Address3: activeAddress.address3,
         City: activeAddress.city,
         State: activeAddress.state,
         Zip: activeAddress.zip,
         CountryCode: activeAddress.countryCode,
         Country: activeAddress.country,
         Contact: activeAddress.contact,
         Phone: tmpPhone + activeAddress.phoneExt, // Don't duplicate extensions
         Fax: activeAddress.fax,
         ContactEmail: _.join(emailList, ";"),
      };

      api
         .post(`${context}/SaveAddress`, payload)
         .then((response) => {
            if (response.data.success) {
               refreshData();
               if (_.isString(response?.data?.message)) {
                  toast.success(response?.data?.message);
                  navigate(-1);
               }
            } else {
               if (_.isString(response?.data?.message)) {
                  toast.error(response?.data?.message);
               }
            }
         })
         .catch(api.catchHandler);
   }

   function loadAddress(addressType) {
      const address = _.find(
         addresses,
         (address) => address.addressCode === addressType
      );
      setActiveAddress(address);
      setEmailList(_.filter(address.emailList, email => !_.isNil(email) && email.length > 0));
      if (_.includes(["US", "CA", "CANADA"], address.countryCode)) {
         setShowStateDropdown(true);
         if (address.countryCode === "US") {
            setStateList(constants.STATES);
         } else {
            setStateList(constants.PROVINCES);
         }
      } else {
         setShowStateDropdown(false);
      }
   }

   function refreshData() {
      // Context is coming in as either Customer or Purchasing(for Vendors).  The address editor is used for both of them,
      // so putting that on the url params lets us dynamically determine which controller to hit.
      api
         .fetch(`${context}/GetAllAddressInfo/${id}`)
         .then((response) => {
            if (response.data.success) {
               setCountryList(
                  _.chain(response.data.countries)
                     .map((ctry) => {
                        return { label: ctry.text, value: ctry.id };
                     })
                     .sortBy((x) => {
                        return x.label;
                     })
                     .value()
               );
               setAccountInfo(response.data.accountInfo);
               setAddresses(response.data.addresses);
            }
         })
         .catch(api.catchHandler);
   }

   function tryAddEmail(updatedEmail) {
      if (helpers.emailIsValid(updatedEmail)) {
         setEmailInvalid(false);
         const emailListCopy = _.slice(emailList);

         if (
            _.findIndex(emailListCopy, (currentEmail) => {
               return currentEmail === updatedEmail;
            }) !== -1
         ) {
            return;
         }

         if (editIndex === null) {
            emailListCopy.push(newEmail);
            setNewEmail("");
         } else {
            emailListCopy[editIndex] = updatedEmail;
            setEditIndex(null);
         }
         setEmailList(emailListCopy);
      } else {
         setEmailInvalid(true);
      }
   }

   return (
      <DesktopContainer screenName={"Edit Address"}>
         <Row className="justify-content-center">
            <Col xs="8">
               <Form onSubmit={handleSubmit}>
                  <FormGroup row>
                     <Label children={`${context === "Customer" ? "Customer" : "Vendor"}:`} xs={2} className="fw-bold" />
                     <Label children={context === "Customer" ? accountInfo && accountInfo.name : accountInfo && accountInfo.vendorNameNumber} xs={8} />
                     <Col xs={2}>
                        <Button className="shredButton float-end" children="Back" onClick={() => navigate(-1)} />
                     </Col>
                  </FormGroup>
                  <FormGroup row>
                     <Label children="Address:" xs={2} className="fw-bold" />
                     <Col xs={10}>
                        <Select
                           classNamePrefix="serviceThreadSelect"
                           id="addressType"
                           options={_.map(addresses, (adr) => {
                              return { value: adr.addressCode, label: adr.ddlText };
                           })}
                           onChange={(opt) => loadAddress(opt.value)}
                        />
                     </Col>
                  </FormGroup>
                  <FormGroup row>
                     <Label children="Address 1:" xs={2} className="fw-bold" />
                     <Col xs={10}>
                        <Input
                           id="address1"
                           value={(activeAddress && activeAddress.address1) || ""}
                           onChange={(e) => handleChange("address1", e.target.value)}
                        />
                     </Col>
                  </FormGroup>
                  <FormGroup row>
                     <Label children="Address 2:" xs={2} className="fw-bold" />
                     <Col xs={10}>
                        <Input
                           id="address2"
                           value={(activeAddress && activeAddress.address2) || ""}
                           onChange={(e) => handleChange("address2", e.target.value)}
                        />
                     </Col>
                  </FormGroup>
                  <FormGroup row>
                     <Label children="Address 3:" xs={2} className="fw-bold" />
                     <Col xs={10}>
                        <Input
                           id="address3"
                           value={(activeAddress && activeAddress.address3) || ""}
                           onChange={(e) => handleChange("address3", e.target.value)}
                        />
                     </Col>
                  </FormGroup>
                  <FormGroup row>
                     <Label children="City:" xs={2} className="fw-bold" />
                     <Col xs={10}>
                        <Input
                           id="city"
                           value={(activeAddress && activeAddress.city) || ""}
                           onChange={(e) => handleChange("city", e.target.value)}
                        />
                     </Col>
                  </FormGroup>
                  <FormGroup row>
                     <Label children="State:" xs={2} className="fw-bold" />
                     <Col xs={10}>
                        {showStateDropdown ? (
                           <Select
                              classNamePrefix="serviceThreadSelect"
                              id="state"
                              options={stateList}
                              value={
                                 activeAddress &&
                                 activeAddress.state && {
                                    value: activeAddress.state,
                                    label: activeAddress.state,
                                 }
                              }
                              onChange={(e) => handleChange("state", e.value)}
                              isClearable
                           />
                        ) : (
                           <Input
                              value={(activeAddress && activeAddress.state) || ""}
                              onChange={(e) => handleChange("state", e.target.value)}
                           />
                        )}
                     </Col>
                  </FormGroup>
                  <FormGroup row>
                     <Label children="Zip Code:" xs={2} className="fw-bold" />
                     <Col xs={10}>
                        <Input
                           id="zip"
                           value={(activeAddress && activeAddress.zip) || ""}
                           onChange={(e) => handleChange("zip", e.target.value)}
                        />
                     </Col>
                  </FormGroup>
                  <FormGroup row>
                     <Label children="Country:" xs={2} className="fw-bold" />
                     <Col xs={10}>
                        <Select
                           classNamePrefix="serviceThreadSelect"
                           id="country"
                           options={countryList}
                           value={
                              activeAddress && activeAddress.countryCode
                                 ? _.find(
                                    countryList,
                                    (c) => c.value === activeAddress.countryCode
                                 )
                                 : ""
                           }
                           onChange={handleCountryChange}
                        />
                     </Col>
                  </FormGroup>
                  <FormGroup row>
                     <Label children="Contact:" xs={2} className="fw-bold" />
                     <Col xs={10}>
                        <Input
                           id="contact"
                           value={(activeAddress && activeAddress.contact) || ""}
                           onChange={(e) => handleChange("contact", e.target.value)}
                        />
                     </Col>
                  </FormGroup>
                  <FormGroup row>
                     <Label children="Phone:" xs={2} className="fw-bold" />
                     <Col xs={4}>
                        <NumberFormat
                           id="phone"
                           className="form-control"
                           format="(###) ###-####"
                           mask="_"
                           maxLength="15"
                           value={(activeAddress && activeAddress.phone) || ""}
                           onValueChange={(values) => { handleChange("phone", values.value); }}
                        />
                     </Col>
                     <Label children="Ext:" xs={2} className="fw-bold" />
                     <Col xs={4}>
                        <Input
                           id="extension"
                           value={(activeAddress && activeAddress.phoneExt) || ""}
                           onChange={(e) => handleChange("phoneExt", e.target.value)}
                        />
                     </Col>
                  </FormGroup>
                  <FormGroup row>
                     <Label children="Fax:" xs={2} className="fw-bold" />
                     <Col xs={10}>
                        <NumberFormat
                           id="fax"
                           className="form-control"
                           format="(###) ###-####"
                           mask="_"
                           maxLength="15"
                           value={(activeAddress && activeAddress.fax) || ""}
                           onValueChange={(values) => { handleChange("fax", values.value); }}
                        />
                     </Col>
                  </FormGroup>
                  <FormGroup row>
                     <Label children="Contact Emails:" xs={2} className="fw-bold" />
                     <Col xs={8}>
                        <Input
                           id="newEmail"
                           value={newEmail || ""}
                           onChange={(e) => {
                              setNewEmail(e.target.value);
                              if (!e.target.value) setEmailInvalid(false);
                           }}
                           invalid={emailInvalid && newEmail}
                        />
                        {emailInvalid && newEmail && (
                           <FormFeedback children="Invalid email. Please correct and try again." />
                        )}
                     </Col>
                     <Col xs={2}>
                        <Button className="shredButton w-100" children="Add Email" onClick={() => tryAddEmail(newEmail)} />
                     </Col>
                  </FormGroup>
                  {_.map(emailList, (email, index) => {
                     return (
                        <Row key={index}>
                           <Col xs={{ size: 8, offset: 2 }}>
                              <FormGroup>
                                 <Input
                                    value={editIndex === index ? editCopy : email}
                                    onChange={(e) => setEditCopy(e.target.value)}
                                    invalid={emailInvalid && editIndex === index}
                                    className={editIndex === index ? "" : "readonly-input"}
                                    readOnly={editIndex !== index}
                                 />
                                 {emailInvalid && editIndex === index && (
                                    <FormFeedback
                                       visible={emailInvalid}
                                       children="Invalid email. Please correct and try again."
                                    />
                                 )}
                              </FormGroup>
                           </Col>
                           <Col className="text-center" xs={2}>
                              <ButtonGroup>
                                 <Button
                                    className="icon-button me-3"
                                    color={editIndex === index ? "success" : "info"}
                                    onClick={
                                       editIndex === index
                                          ? () => tryAddEmail(editCopy)
                                          : () => allowEmailEdit(index)
                                    }
                                    disabled={editIndex !== null && editIndex !== index}
                                 >
                                    <FontAwesomeIcon className="whiteIcon" icon={editIndex === index ? "check" : "edit"} />
                                 </Button>
                                 <Button
                                    className="icon-button"
                                    color={editIndex === index ? "secondary" : "danger"}
                                    onClick={
                                       editIndex === index
                                          ? () => cancelEmailEdit()
                                          : () => setEmailToDelete(email)
                                    }
                                    title={
                                       editIndex === index
                                          ? "Discard Changes"
                                          : "Delete Email"
                                    }
                                    disabled={editIndex !== null && editIndex !== index}
                                 >
                                    <FontAwesomeIcon className="whiteIcon" icon={editIndex === index ? "times" : "trash"} />
                                 </Button>
                              </ButtonGroup>
                           </Col>
                        </Row>
                     );
                  })}
                  <FormGroup row>
                     <Col xs={{ size: 2, offset: 10 }}>
                        <Button
                           className="shredButton"
                           style={{ width: "100%" }}
                           type="submit"
                           children="Save Changes"
                        />
                     </Col>
                  </FormGroup>
               </Form>
            </Col>
         </Row>

         <Modal isOpen={emailToDelete !== null} contentClassName={modalClassName}>
            <ModalHeader children="Confirm Email Deletion" />
            <ModalBody children={`Are you sure you want to delete ${emailToDelete}?`} />
            <ModalFooter>
               <ButtonGroup>
                  <Button className="button-success" children="Confirm" onClick={deleteEmail} />
                  <Button className="button-danger" children="Cancel" onClick={() => setEmailToDelete(null)} />
               </ButtonGroup>
            </ModalFooter>
         </Modal>
      </DesktopContainer>
   );
}
